import {
  useForm,
  useWatch,
  FormProvider,
  useFieldArray
} from "react-hook-form";
import {
  Col,
  Row,
  Form,
  Button,
  Spinner,
  Tooltip,
  ProgressBar,
  OverlayTrigger
} from "react-bootstrap";
import styled from "styled-components";
import { useParams } from "react-router";
import {
  faUpload,
  faTrashAlt,
  faWandMagicSparkles
} from "@fortawesome/free-solid-svg-icons";
import { useState, useMemo, useEffect } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Loader from "../../loader/loader";
import {
  saveLocalSeos,
  deleteLocalSeo,
  getCustomerCity,
  deleteLocalSeoById,
  updatePublicSeoSite,
  fetchCitySubDistricts,
  generateLocalSeoContent,
  fetchWebsiteLocalSeosByCustomer
} from "../../../services/api-declaration";
import { PageHeading } from "../../common-styling";
import ServiceCard from "./local-seo/service-card";
import { useDialog } from "../../../hooks/useDialog";
import { useTabRoute } from "../../../hooks/useTabRoute";
import FieldLabelCol from "./local-seo/field-label-column";
import PreventReload from "../../funnel-form/prevent-reload";
import AddNewFieldButton from "./local-seo/add-new-field-button";
import DynamicFieldCityRow from "./local-seo/dynamic-field-city-row";
import { SUB_PAGE_STATUS, LOCAL_SEO_FIELDS_NAME } from "../../strings";
import { showError, showSuccess } from "../../../services/toast-service";
import DynamicFieldKeywordRow from "./local-seo/dynamic-field-keyword-row";
import { LocalSeoSchema } from "../website-schema/local-seo-validation-schema";
import { useTranslation } from "react-i18next";

const ShadowRow = styled(Row)`
  box-shadow: rgba(0, 0, 0, 0.35) 0px -50px 25px -60px inset;
`;
const SubDistrictsContainer = styled.div`
  width: 90%;
  max-height: 150px;
  overflow-y: scroll;
  padding-left: 38px;

  /* move scrollbar to the left */
  direction: rtl;

  /* keep content visually normal */
  & > * {
    direction: ltr;
  }
`;

const { cities, keywords } = LOCAL_SEO_FIELDS_NAME;
const defaultFormVals = {
  [cities]: [{ value: "" }],
  [keywords]: [{ value: "" }]
};

const LocalSEO = ({ isTabSelected, isMainPublished }) => {
  const { t } = useTranslation();
  const { tabRoute } = useTabRoute();
  const { id: customerId } = useParams();
  const { showConfirmationDialogBox } = useDialog();
  const [progressState, setProgressState] = useState({
    visible: false,
    percent: 0,
    label: ""
  });
  const [generatedSubPageGrid, setGeneratedSubPageGrid] = useState({});
  const [isPublishing, setIsPublishing] = useState(false);
  const [isCityPopulating, setIsCityPopulating] = useState(false);
  const [isLoadingFormData, setIsLoadingFormData] = useState(true);
  const [isFetchingSubDistricts, setIsFetchingSubDistricts] = useState(false);
  const [fetchingCity, setFetchingCity] = useState(null);
  const [fetchedCities, setFetchedCities] = useState({});
  const hasSavedSeoPages = Object.keys(generatedSubPageGrid || {}).length > 0;
  const shouldDisablePublishBtn =
    !isMainPublished || !hasSavedSeoPages || isPublishing || isCityPopulating;

  const formMethods = useForm({
    resolver: yupResolver(LocalSeoSchema),
    defaultValues: defaultFormVals
  });
  const {
    reset,
    control,
    setValue,
    handleSubmit,
    formState: { errors, isSubmitting }
  } = formMethods;

  /* FIELD ARRAYS */
  const {
    fields: cityFields,
    prepend: prependCityHandler,
    append: appendCityHandler,
    remove: removeCityHandler
  } = useFieldArray({ control, name: cities });
  const {
    fields: keywordFields,
    append: appendKeywordHandler,
    remove: removeKeywordHandler
  } = useFieldArray({ control, name: keywords });

  /* WATCHERS */
  const cityValues = useWatch({ control, name: cities });
  const keywordValues = useWatch({ control, name: keywords });

  const canAddCityField =
    !cityValues.length || cityValues[cityValues.length - 1].value?.trim();
  const canAddKeywordField =
    !keywordValues.length ||
    keywordValues[keywordValues.length - 1].value?.name?.trim();

  /* HELPERS */
  const _resetFormToDefault = () => reset(defaultFormVals);
  const _hideProgressWithDelay = () =>
    setTimeout(
      () => setProgressState({ visible: false, percent: 100, label: "" }),
      800
    );
  const _updateProgress = (completed, total) => {
    const percent = Math.max(15, Math.round((completed / total) * 100));
    setProgressState({
      visible: true,
      percent,
      label: `${completed} / ${total} ${t("localSeo.progressCompleted")}`
    });
  };
  const _normalizeCityTextFields = (dataList) =>
    dataList.map((entry, index) => {
      const normalized = entry.value.trim().replace(/\s+/g, " ");
      setValue(`${cities}.${index}.value`, normalized, {
        shouldValidate: true
      });
      return {
        id: cityFields[index].id,
        value: normalized,
        parentCity: entry.parentCity || null
      };
    });
  const _updateSubPageGrid = ({ city, keywordId, data }) => {
    setGeneratedSubPageGrid((prevGrid) => {
      // get current city array
      const cityArray = prevGrid[city] || [];
      // map and update only matching keyword
      const updatedCityArray = cityArray.map((item) => {
        if (!item) return item; // handle nulls in array
        if (item?.keyword?._id === keywordId || item?.keyword === keywordId) {
          return { ...item, ...data }; // merge new data
        }
        return item; // keep others as it is
      });
      // return updated grid object
      return { ...prevGrid, [city]: updatedCityArray };
    });
  };
  const _removeCityFormField = (cityName) => {
    const index = cityFields.findIndex((field) => field.value === cityName);
    if (index !== -1) {
      removeCityHandler(index);
    }
    // if all cities are removed, reset both cities and keywords fields
    if (cityFields.length <= 1) {
      _resetFormToDefault();
    }
  };
  const _removeKeywordFormField = (keywordId) => {
    const index = keywordFields.findIndex(
      (field) => field.value._id === keywordId
    );
    if (index !== -1) {
      removeKeywordHandler(index);
    }
    // if all keywords are removed, reset both cities and keywords fields
    if (keywordFields.length <= 1) {
      _resetFormToDefault();
    }
  };
  const _generateRespGridAndCityMeta = (respData) => {
    const grid = {};
    const generatedKeywords = {};
    const cityMeta = {}; // store extra city info

    respData.forEach((item) => {
      grid[item.city] = grid[item.city] || [];
      grid[item.city].push(item);

      generatedKeywords[item.keyword._id] = item.keyword;

      // store parentCity per city
      if (!cityMeta[item.city]) {
        cityMeta[item.city] = {
          parentCity: item.parentCity || null
        };
      }
    });

    return { grid, generatedKeywords, cityMeta };
  };
  const _resetFormAndSetGrid = (respCollection) => {
    const { grid, generatedKeywords, cityMeta } =
      _generateRespGridAndCityMeta(respCollection);

    reset({
      cities: Object.keys(grid).map((_city) => ({
        value: _city,
        disabled: true,
        parentCity: cityMeta[_city]?.parentCity || null
      })),
      keywords: Object.values(generatedKeywords).map((_keyword) => ({
        value: _keyword,
        disabled: true
      }))
    });
    // set state for grouped generated sub pages
    setGeneratedSubPageGrid(grid);

    // detect fetched cities
    const _fetchedCities = {};
    Object.entries(cityMeta).forEach(([_, meta]) => {
      if (meta.parentCity) {
        _fetchedCities[meta.parentCity.toLowerCase()] = true;
      }
    });
    setFetchedCities(_fetchedCities);
  };

  const filterGenerateContentRequest = (generatedSubPages) => {
    let pendingCount = 0;
    let parallelRequests = [];
    for (const page of generatedSubPages) {
      if ([SUB_PAGE_STATUS.NEW, SUB_PAGE_STATUS.FAILED].includes(page.status)) {
        pendingCount++;
        parallelRequests.push(page);
      }
    }
    return { pendingCount, parallelRequests };
  };
  const updateAfterEditSuccess = ({ subPageEntry, seo, sections }) =>
    _updateSubPageGrid({
      city: subPageEntry.city,
      keywordId: subPageEntry.keyword._id,
      data: {
        data: { ...subPageEntry.data, seo, sections }
      }
    });

  /* API INTEGRATIONS */
  const handlePopulateCustomerCity = async () => {
    try {
      setIsCityPopulating(true);
      const response = await getCustomerCity({ customerId });
      const detectedCityName = response?.data?.data?.city?.name;
      if (detectedCityName) {
        removeCityHandler(0);
        prependCityHandler({ value: detectedCityName });
      }
    } finally {
      setIsCityPopulating(false);
    }
  };

  const generateLocalSeo = async (entry) => {
    // To set status only
    _updateSubPageGrid({
      city: entry.city,
      keywordId: entry.keyword._id,
      data: { status: SUB_PAGE_STATUS.PENDING }
    });
    try {
      const response = await generateLocalSeoContent({
        subPageId: entry._id,
        customerId,
        city: entry.city,
        keyword: {
          _id: entry.keyword._id,
          name: entry.keyword.name
        },
        parentCity: entry.parentCity || null
      });
      _updateSubPageGrid({
        city: entry.city,
        keywordId: entry.keyword._id,
        data: {
          data: response.data?.data?.updatedPayload?.data,
          aiResponseError: null,
          status: SUB_PAGE_STATUS.SUCCESS
        }
      });
    } catch (error) {
      _updateSubPageGrid({
        city: entry.city,
        keywordId: entry.keyword._id,
        data: {
          data: null,
          aiResponseError: error,
          status: SUB_PAGE_STATUS.FAILED
        }
      });
    }
  };

  /* SUBMIT */
  const handleSubmitLocalSeos = async (formData) => {
    setProgressState({
      visible: true,
      percent: 5,
      label: t("localSeo.progressStarting")
    });
    const cleanedCities = _normalizeCityTextFields(formData.cities); // because it's a text field value
    const savePayload = {
      customerId,
      tabRoute,
      cities: cleanedCities.map((_city) => ({
        value: _city.value,
        parentCity: _city.parentCity
      })),
      keywords: formData.keywords.map((_keyword) => _keyword.value)
    };
    // Save form data for local seo
    let saveResponseData = [];
    let completed = 0;
    let pendingCount = 0;
    let parallelRequests = [];

    try {
      // Save local seos
      const saveResponse = await saveLocalSeos(savePayload);
      saveResponseData = saveResponse.data?.data;
      // reset form vals and update state
      _resetFormAndSetGrid(saveResponseData);
      ({ pendingCount, parallelRequests } =
        filterGenerateContentRequest(saveResponseData));
      _updateProgress(completed, pendingCount);
      showSuccess(saveResponse.data?.message || "Local Seo Saved!");
    } catch (error) {
      console.log("Error in saving local seo content! ", error);
      showError(
        error.response?.data?.message || "Error in saving local seo content!"
      );
    }
    // Generate local seo sub pages through AI (with parallel api requests).
    await Promise.all(
      parallelRequests.map(async (entry) => {
        try {
          await generateLocalSeo(entry);
        } finally {
          completed++;
          _updateProgress(completed, pendingCount);
        }
      })
    );
    _hideProgressWithDelay();
  };

  const handleDeleteKeyword = async (keywordId) => {
    try {
      const response = await deleteLocalSeo({ keywordId, tabRoute });
      if (response) {
        setGeneratedSubPageGrid((prevGrid) => {
          let clone = {};
          Object.entries(prevGrid).forEach(([city, keywords]) => {
            const updatedKeywords = keywords.filter(
              ({ keyword }) => keyword._id !== keywordId
            );
            if (updatedKeywords.length) {
              clone[city] = updatedKeywords;
            }
          });
          return clone;
        });
        _removeKeywordFormField(keywordId);
      }
    } catch (error) {
      console.log("Error in delete by keyword: ", error);
      showError(error.response?.data?.message || "Error in delete by keyword!");
    }
  };

  const handleDeleteCity = async (cityName) => {
    try {
      const response = await deleteLocalSeo({ cityName, tabRoute });
      if (response) {
        setGeneratedSubPageGrid((prevGrid) => {
          delete prevGrid[cityName];
          return { ...prevGrid };
        });
        _removeCityFormField(cityName);
      }
    } catch (error) {
      console.log("Erro in delete by city: ", error);
      showError(error.response?.data?.message || "Error in delete by city!");
    }
  };

  const toggleDeleteCityConfirmation = (cityName) =>
    showConfirmationDialogBox({
      dialogType: "danger",
      title: t("localSeo.delDialogHeading"),
      dialogMessage: t("localSeo.renderDelGeneratedCityDialogMsg"),
      responseAction: (confirmed) => {
        if (confirmed) {
          handleDeleteCity(cityName);
        }
      }
    });

  const handleDeleteSubPage = async (subPageEntry) => {
    try {
      const response = await deleteLocalSeoById(subPageEntry._id);
      if (response) {
        setGeneratedSubPageGrid((prevGrid) => {
          let updatedGrid = {};
          const cityArray = prevGrid[subPageEntry.city] || [];
          const updatedCityArray = cityArray.filter(
            (item) => item._id !== subPageEntry._id
          );
          if (!updatedCityArray.length) {
            // If no keywords left for the city, remove the city key entirely
            delete prevGrid[subPageEntry.city];
            updatedGrid = { ...prevGrid };
            _removeCityFormField(subPageEntry.city);
          } else {
            updatedGrid = {
              ...prevGrid,
              [subPageEntry.city]: updatedCityArray
            };
          }
          const keywordId = subPageEntry.keyword._id;
          // if no other sub page is using the keyword, remove keyword field
          const hasNoKeyword = !Object.values(updatedGrid).some(
            (keywordsArray) => {
              return keywordsArray.some(
                (item) => item?.keyword?._id === keywordId
              );
            }
          );
          if (hasNoKeyword) {
            _removeKeywordFormField(keywordId);
          }
          return updatedGrid;
        });
      }
    } catch (error) {
      console.log("Error in delete by seoId: ", error);
      showError(error.response?.data?.message || "Error in delete by page id!");
    }
  };

  const handleRetrySubPage = async (subPageEntry) => {
    try {
      setProgressState({
        visible: true,
        percent: 25,
        label: t("localSeo.progressRetrying")
      });
      await generateLocalSeo(subPageEntry);
    } finally {
      setProgressState({
        visible: true,
        percent: 100,
        label: t("localSeo.progressRetrySuccess")
      });
    }
    _hideProgressWithDelay();
  };

  const handleSubDistrictFetch = async (city) => {
    const normalizedCity = city?.toLowerCase();
    if (!normalizedCity || fetchedCities[normalizedCity]) return;
    setIsFetchingSubDistricts(true);
    setFetchingCity(normalizedCity);
    try {
      const response = await fetchCitySubDistricts({ city });
      const districts = response?.data?.data || [];
      const newCities = districts.map((district) => ({
        value: district,
        disabled: false,
        parentCity: normalizedCity
      }));
      if (newCities.length) {
        appendCityHandler(newCities);
      }
      setFetchedCities((prev) => ({
        ...prev,
        [normalizedCity]: true
      }));
      showSuccess(`Sub-districts for ${city} fetched successfully!`);
    } catch (err) {
      console.error(err);
      showError(`Failed to fetch sub-districts for ${city}`);
    } finally {
      setIsFetchingSubDistricts(false);
      setFetchingCity(null);
    }
  };

  const updatePublicSite = async () => {
    try {
      setIsPublishing(true);
      const response = await updatePublicSeoSite({ customerId });
      showSuccess(
        response.data?.message || "Public site updated with latest changes!"
      );
    } catch (err) {
      console.log("Error in revalidating public site: ", err);
      showError("Failed to update public site with latest local seo changes");
    } finally {
      setIsPublishing(false);
    }
  };

  // Fetch saved local seo, fetch keyword options and populate form fields
  useEffect(() => {
    const fetchAndResetLocalSeos = async () => {
      try {
        const response = await fetchWebsiteLocalSeosByCustomer({
          customerId,
          tabRoute
        });
        const respData = response?.data?.data || [];
        if (!respData.length) {
          _resetFormToDefault();
          setGeneratedSubPageGrid({});
          return;
        }
        _resetFormAndSetGrid(respData);
      } catch (error) {
        console.log("Error in fetchAndResetForm: ", error);
        showError(error.response?.data?.message || "Error in fetch results!");
      } finally {
        setIsLoadingFormData(false);
      }
    };
    if (isTabSelected) {
      fetchAndResetLocalSeos();
    }
    // eslint-disable-next-line
  }, [reset, customerId, isTabSelected, tabRoute]);

  /* Filter already selected keyword options to exclude */
  const selectedKeywordIds = useMemo(
    () =>
      (keywordValues || [])
        .map((k) => k?.value?.id || k?.value?._id)
        .filter(Boolean),
    [keywordValues]
  );
  const groupedCities = useMemo(() => {
    const map = {};
    cityFields.forEach((field, index) => {
      const cityName = field.value;
      const parent = field.parentCity;
      if (!parent) {
        // main city
        if (!map[cityName]) {
          map[cityName] = {
            index: index,
            children: []
          };
        } else {
          map[cityName].index = index;
        }
      } else {
        // sub-district
        if (!map[parent]) {
          map[parent] = {
            index: null,
            children: []
          };
        }
        map[parent].children.push({
          field: field,
          index: index
        });
      }
    });
    return map;
  }, [cityFields]);

  return isLoadingFormData ? (
    <Loader />
  ) : (
    <div className="mx-1">
      <FormProvider {...formMethods}>
        <PreventReload />
        <Form onSubmit={handleSubmit(handleSubmitLocalSeos)}>
          <fieldset disabled={isSubmitting || progressState.visible}>
            <div className="d-flex align-items-center justify-content-between flex-column flex-md-row py-2">
              <PageHeading className="mt-0 ms-0">
                {t("localSeo.pageTitle")}
              </PageHeading>
              <div>
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip>
                      {!isMainPublished
                        ? "Main website is not published"
                        : "Publish seo pages"}
                    </Tooltip>
                  }
                >
                  <span className="d-inline-block">
                    <Button
                      variant="dark"
                      className="me-2"
                      onClick={updatePublicSite}
                      disabled={shouldDisablePublishBtn}
                    >
                      {isPublishing ? (
                        <Spinner size="sm" animation="border" />
                      ) : (
                        <FontAwesomeIcon icon={faUpload} />
                      )}
                      <span className="ms-2">Publish</span>
                    </Button>
                  </span>
                </OverlayTrigger>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={isCityPopulating || isPublishing}
                >
                  <FontAwesomeIcon icon={faWandMagicSparkles} />
                  <span className="ms-2">{t("localSeo.submitBtn")}</span>
                </Button>
              </div>
            </div>

            {/* CITY FIELDS */}
            <Row className="mb-2">
              <FieldLabelCol
                label={t("localSeo.locationLabel")}
                hasErrorMsg={errors?.[cities]?.root?.message}
              />
              <Col sm={12} md={10}>
                {Object.entries(groupedCities).map(([cityName, cityData]) => (
                  <div key={cityName} className="mb-3">
                    {/* MAIN CITY */}
                    {cityData.index !== null && (
                      <div className="d-flex align-items-center mb-2">
                        <DynamicFieldCityRow
                          field={{
                            name: cities,
                            index: cityData.index,
                            value: cityFields[cityData.index].value,
                            removeHandler: removeCityHandler,
                            isGenerated: cityFields[cityData.index].disabled,
                            parentCity: null,
                            placeholder: t("localSeo.locationPlaceholder"),
                            error: errors?.[cities]?.[cityData.index]?.value,
                            isPopulating: isCityPopulating,
                            onAfterDelete: () =>
                              handleDeleteCity(
                                cityFields[cityData.index].value
                              ),
                            onPopulateClick: handlePopulateCustomerCity,
                            isFetchingSubDistricts,
                            fetchingCity,
                            fetchedCities,
                            onFetchSubDistricts: handleSubDistrictFetch
                          }}
                        />
                      </div>
                    )}

                    {/* CITY SUB-DISTRICTS */}
                    {cityData.children.length > 0 && (
                      <SubDistrictsContainer className="mb-4">
                        <div className="text-muted small mb-1">
                          Sub-districts
                        </div>
                        {cityData.children.map(({ field, index }) => (
                          <div
                            key={index}
                            className="mb-2 d-flex align-items-center"
                          >
                            <DynamicFieldCityRow
                              field={{
                                name: cities,
                                index,
                                value: field.value,
                                removeHandler: removeCityHandler,
                                isGenerated: field.disabled,
                                parentCity: field.parentCity,
                                placeholder: t("localSeo.locationPlaceholder"),
                                error: errors?.[cities]?.[index]?.value,
                                isFetchingSubDistricts,
                                fetchingCity,
                                fetchedCities
                              }}
                            />
                          </div>
                        ))}
                      </SubDistrictsContainer>
                    )}
                  </div>
                ))}

                <AddNewFieldButton
                  handleClick={appendCityHandler}
                  isDisabled={isCityPopulating || !canAddCityField}
                  btnText={t("localSeo.addNewLocationBtn")}
                />
              </Col>
            </Row>

            {/* KEYWORD FIELDS */}
            <Row className="mb-2 mt-4">
              <FieldLabelCol
                label={t("localSeo.serviceKeywordLabel")}
                hasErrorMsg={errors?.[keywords]?.root?.message}
              />
              <Col sm={12} md={10}>
                {keywordFields.map((keywordField, keywordIndex) => {
                  const currentId =
                    keywordValues?.[keywordIndex]?.value?.id ||
                    keywordValues?.[keywordIndex]?.value?._id;
                  return (
                    <div
                      key={keywordField.id}
                      className="mb-2 d-flex align-items-center"
                    >
                      <DynamicFieldKeywordRow
                        field={{
                          name: keywords,
                          index: keywordIndex,
                          removeHandler: removeKeywordHandler,
                          isGenerated: keywordField.disabled,
                          placeholder: t("localSeo.serviceKeywordPlaceholder"),
                          error: errors?.[keywords]?.[keywordIndex]?.value,
                          onAfterDelete: () =>
                            handleDeleteKeyword(keywordField.value._id),
                          excludeIds: selectedKeywordIds.filter(
                            (id) => id !== currentId
                          )
                        }}
                      />
                    </div>
                  );
                })}
                <AddNewFieldButton
                  handleClick={appendKeywordHandler}
                  isDisabled={!canAddKeywordField}
                  btnText={t("localSeo.addNewKeywordBtn")}
                />
              </Col>
            </Row>
          </fieldset>
        </Form>
      </FormProvider>

      {/* GENERATED RESULTS */}
      {Object.keys(generatedSubPageGrid)?.length > 0 && (
        <div className="mb-4">
          {/* PROGRESS BAR */}
          {progressState.visible && (
            <ProgressBar
              className="mt-3"
              variant="primary"
              now={progressState.percent}
              label={progressState.label}
              striped
              animated
            />
          )}
          <hr className="mt-4 mb-0" />
          {Object.entries(generatedSubPageGrid).map(
            ([cityLabel, subPagesForCity]) => (
              <ShadowRow key={cityLabel} className="px-2">
                <div className="my-2 d-flex align-items-center justify-content-between">
                  <div className="fw-bold text-capitalize">{cityLabel}</div>
                  <FontAwesomeIcon
                    size="lg"
                    icon={faTrashAlt}
                    className={`cursor-pointer ${
                      progressState.visible ? "pe-none text-secondary" : ""
                    }`}
                    onClick={() => toggleDeleteCityConfirmation(cityLabel)}
                  />
                </div>
                {subPagesForCity.map((subPageEntry, subPageIndex) => (
                  <Col sm={12} md={2} key={subPageIndex} className="mb-4">
                    <ServiceCard
                      data={subPageEntry}
                      isActionDisabled={progressState.visible}
                      onAfterRetrySubPage={() =>
                        handleRetrySubPage(subPageEntry)
                      }
                      onAfterDeleteSubPage={() =>
                        handleDeleteSubPage(subPageEntry)
                      }
                      handleAfterEditSuccess={({ seo, sections }) =>
                        updateAfterEditSuccess({ subPageEntry, seo, sections })
                      }
                    />
                  </Col>
                ))}
              </ShadowRow>
            )
          )}
        </div>
      )}
    </div>
  );
};

export default LocalSEO;
